import { klona } from 'klona'
import { Config, Flow } from '@/types/config'
import { Template } from '@/types/template'

export const configFromTemplate = (template: Template) => {
  const { flow, offers, phrases, questions, segmentGroups } =
    template.templateDetails.json

  const widget = {
    id: 'wdg_default',
    contentId: flow.id,
    name: 'Default Widget',
    type: 'inline',
    options: {
      font: 'google*Lato',
      theme: 'plain',
      palette: {
        borderColor: '#000000',
        contrastColor: '#3182CE',
        backgroundColor: '#ffffff',
        textOnContrastColor: '#EBF8FF',
        textOnBackgroundColor: '#1A202C'
      },
      fontSize: {
        lineHeight: 1.5,
        headlineFontSize: '24px',
        formFieldFontSize: '13px',
        descriptionFontSize: '16px',
        answerButtonFontSize: '13px',
        submitButtonFontSize: '13px'
      },
      customCss: '',
      structure: {
        formFieldSpacing: '8px',
        buttonBorderRadius: '0px',
        answerButtonSpacing: '8px',
        containerBorderWidth: '0px',
        containerBorderRadius: '8px'
      },
      displayTargets: {
        phones: true,
        desktop: true,
        tablets: true
      },
      backButtonLabel: '← Go back',
      backButtonEnabled: true
    },
    isEnabled: true,
    visibility: {
      areas: 'optin',
      blacklist: [],
      whitelist: [],
      isBlacklistEnabled: true,
      isWhitelistEnabled: false
    },
    whenToAskNext: 'startover'
  }

  const newConfig = {
    version: 'PREVIEW',
    phrases,
    dimensions: segmentGroups,
    rightAsk: { questions },
    rightCta: { offers, offerFunnels: [flow], widgets: [widget] }
  } as Config

  return newConfig
}

export const installTemplate = (template: object) => {
  const { flow, offers, phrases, questions, segmentGroups, widgets } = template

  const configStore = useConfigStore()
  const flowStore = useFlowStore()
  const widgetStore = useWidgetStore()

  // Create maps to store temporary to new IDs
  const segmentGroupMap = new Map<string, string>()
  const questionMap = new Map<string, string>()
  const offerMap = new Map<string, string>()
  const phraseMap = new Map<string, string>()
  const segmentMap = new Map<string, string>()

  const { config } = storeToRefs(configStore)

  // Install segment groups and their segments
  segmentGroups?.forEach((sg) => {
    const newId = makeId(idPrefixes.DIMENSION)
    segmentGroupMap.set(sg.id, newId)

    const newSegmentGroup = {
      ...sg,
      id: newId,
      segments: sg.segments.map((segment) => {
        const newSegmentId = makeId(idPrefixes.SEGMENT)
        segmentMap.set(segment.id, newSegmentId)
        return { ...segment, id: newSegmentId }
      }),
      signals: sg.signals.map((signal) => ({
        ...signal,
        indicates: segmentMap.get(signal.indicates) || signal.indicates
      }))
    }

    config.value?.dimensions.push(newSegmentGroup)
  })

  // Install phrases
  phrases?.forEach((phrase) => {
    const newId = makeId(idPrefixes.PHRASE)
    phraseMap.set(phrase.id, newId)

    const newMap = {}
    Object.entries(phrase.map).forEach(([key, value]) => {
      newMap[segmentMap.get(key) || key] = value
    })

    const newPhrase = {
      ...phrase,
      id: newId,
      segmentGroupId:
        segmentGroupMap.get(phrase.segmentGroupId) || phrase.segmentGroupId,
      map: newMap
    }

    config.value?.phrases.push(newPhrase)
  })

  // Install questions
  questions?.forEach((question) => {
    const newId = makeId(idPrefixes.RIGHT_ASK_QUESTION)
    questionMap.set(question.id, newId)

    const newQuestion = {
      ...question,
      id: newId,
      dimensionId:
        segmentGroupMap.get(question.dimensionId) || question.dimensionId,
      text: question.text.replace(
        /phr_tmp_[a-zA-Z0-9]+/g,
        (match) => phraseMap.get(match) || match
      ),
      options: question.options.map((option) => ({
        ...option,
        id: makeId(idPrefixes.RIGHT_ASK_QUESTION_OPTION),
        segmentId: segmentMap.get(option.segmentId) || option.segmentId
      }))
    }

    config.value?.rightAsk.questions.push(newQuestion)
  })

  // Install offers
  offers?.forEach((offer) => {
    const newId = makeId(idPrefixes.OFFER)
    offerMap.set(offer.id, newId)

    const newParts = {}
    Object.entries(offer.parts).forEach(([key, value]) => {
      newParts[key] = value.replace(
        /phr_tmp_[a-zA-Z0-9]+/g,
        (match) => phraseMap.get(match) || match
      )
    })

    const newOffer = {
      ...offer,
      id: newId,
      parts: newParts
    }

    config.value?.rightCta.offers.push(newOffer)
  })

  // Create the flow with updated definition
  const definition = klona(flow.funnelDefinition)

  // Update all references in the funnel definition
  funnelDefinitionEach(definition, (node) => {
    if (node.type === 'sg' && node.segmentGroupId) {
      if (node.split) {
        node.split = node.split.map((split) => {
          if (!split.segmentId) return split
          return {
            ...split,
            segmentId: segmentMap.get(split.segmentId) || split.segmentId
          }
        })
      }

      node.segmentGroupId =
        segmentGroupMap.get(node.segmentGroupId) || node.segmentGroupId
    }

    if (node.type === 'offer' && node.offerId) {
      node.offerId = offerMap.get(node.offerId) || node.offerId
    }

    if (node.type === 'yesno') {
      conditionEach(node.condition, (def) => {
        if (def.$source === 'segments') {
          const oldSegmentGroupId = def.$type
          def.$type =
            segmentGroupMap.get(oldSegmentGroupId) || oldSegmentGroupId

          def.segmentId = def.segmentId.map((id) => segmentMap.get(id) || id)
        }
      })
    }
  })

  const newFlow = flowStore.create({
    name: flow.name,
    funnelDefinition: definition
  })

  // Install widgets if they exist
  widgets?.forEach((widget) => {
    delete widget.id
    const newWidget = {
      ...widget,
      contentId: newFlow.id
    }

    widgetStore.create(newWidget)
  })

  return newFlow
}

export const createTemplateFromFlow = (flow: Flow) => {
  const definition = klona(flow.funnelDefinition)

  const segmentGroupStore = useSegmentGroupStore()
  const questionStore = useQuestionStore()
  const offerStore = useOfferStore()
  const phraseStore = usePhraseStore()

  const segmentGroupIds = new Set<string>()
  const questionIds = new Set<string>()
  const offerIds = new Set<string>()
  const phraseIds = new Set<string>()

  // First, extract out all the offer and segment group IDs
  funnelDefinitionEach(definition, (node) => {
    delete node._uuid

    if (node.type === 'sg' && node.segmentGroupId) {
      segmentGroupIds.add(node.segmentGroupId)

      const question = questionStore.getBySegmentGroupId(node.segmentGroupId)
      if (question) {
        questionIds.add(question.id)
      }
    }

    if (node.type === 'offer') {
      offerIds.add(node.offerId)
    }
  })

  // Now, create duplicates of each offer, phrase, and segment group

  const segmentGroups = Array.from(segmentGroupIds).map((id, idx) => {
    const sg = segmentGroupStore.getById(id)
    // Destructure to exclude syncTo, syncToCustomField, and UI-specific fields
    const {
      syncTo,
      syncToCustomField,
      accessedAt,
      favorited,
      ...sgWithoutExcluded
    } = sg

    // Filter out signals that depend on ESP-specific custom fields or tags
    const filteredSignals = sg.signals.filter((signal) => {
      // Check if the signal definition contains ESP-specific references
      if (signal.definition && typeof signal.definition === 'object') {
        // If it has a $source field (indicating ESP origin) and customFieldId, exclude it
        if (signal.definition.$source && signal.definition.customFieldId) {
          return false
        }
        // If it's a tag-based signal, exclude it
        if (signal.definition.$type === 'tags' || signal.definition.$type === 'tag') {
          return false
        }
        // For nested conditions (and/or), check recursively
        if (signal.definition.and || signal.definition.or) {
          const hasEspSpecificCondition = (condition: any): boolean => {
            if (condition.$source && condition.customFieldId) return true
            if (condition.$type === 'tags' || condition.$type === 'tag') return true
            if (condition.and) return condition.and.some(hasEspSpecificCondition)
            if (condition.or) return condition.or.some(hasEspSpecificCondition)
            return false
          }
          if (hasEspSpecificCondition(signal.definition)) {
            return false
          }
        }
      }
      return true
    })

    return {
      ...sgWithoutExcluded,
      id: `dim_tmp_${idx}`,
      segments: sg.segments.map((s, idx2) => {
        return {
          ...s,
          id: `seg_tmp_${idx}_${idx2}`
        }
      }),
      signals: filteredSignals.map((s, idx2) => {
        return {
          ...s,
          indicates: `seg_tmp_${idx}_${Array.from(sg.segments).findIndex(seg => seg.id === s.indicates)}`
        }
      })
    }
  })

  let questions = Array.from(questionIds).map((id, idx) => {
    const question = questionStore.getById(id)

    const phraseMatches = question.text.match(
      /{{ phrases\.(phr_[a-zA-Z0-9]+) }}/g
    )
    if (phraseMatches) {
      phraseMatches.forEach((match) => {
        const phraseId = match.match(/phr_[a-zA-Z0-9]+/)[0]
        phraseIds.add(phraseId)
      })
    }

    // Destructure to exclude syncTo and syncToField, but keep all other fields
    // This includes: otherOptions, inputType, inputSettings, description, displayOptions, isQuestion
    const { syncTo, syncToField, ...questionWithoutSync } = question

    return {
      ...questionWithoutSync,
      id: `raq_tmp_${idx}`,
      dimensionId: `dim_tmp_${Array.from(segmentGroupIds).indexOf(
        question.dimensionId
      )}`,
      options: question.options.map((o, idx2) => {
        return {
          ...o,
          id: `rqo_tmp_${idx}_${idx2}`,
          segmentId: `seg_tmp_${Array.from(segmentGroupIds).indexOf(
            question.dimensionId
          )}_${idx2}`
        }
      })
    }
  })

  let offers = Array.from(offerIds).map((id, idx) => {
    const offer = offerStore.getById(id)
    if (!offer) return

    const content = Object.values(offer.parts).join(' ')
    const phraseMatches = content.match(/{{ phrases\.(phr_[a-zA-Z0-9]+) }}/g)
    if (phraseMatches) {
      phraseMatches.forEach((match) => {
        const phraseId = match.match(/phr_[a-zA-Z0-9]+/)[0]
        phraseIds.add(phraseId)
      })
    }

    // Clean up the offer and strip _uuid from redirect funnels if they exist
    const cleanOffer = { ...offer }
    if (
      cleanOffer.triggeredAction?.settings?.redirectFunnel &&
      cleanOffer.triggeredAction.settings.redirectFunnel.type === 'root'
    ) {
      // Clone the redirect funnel and remove _uuid from all nodes
      const cleanRedirectFunnel = klona(
        cleanOffer.triggeredAction.settings.redirectFunnel
      )
      funnelDefinitionEach(cleanRedirectFunnel, (node) => {
        delete node._uuid
      })
      cleanOffer.triggeredAction.settings.redirectFunnel = cleanRedirectFunnel
    }

    return {
      ...cleanOffer,
      id: `ofr_tmp_${idx}`
    }
  })

  // TODO: Check offer parts for {{ phr... and remap based on the index in the phrases set }}

  const phrases = Array.from(phraseIds).map((id, idx) => {
    const phrase = phraseStore.getById(id)

    const map = {}
    Object.entries(phrase.map).forEach(([key, value]) => {
      map[
        `seg_tmp_${Array.from(segmentGroupIds).indexOf(
          phrase.segmentGroupId
        )}_${Array.from(
          segmentGroupStore.getById(phrase.segmentGroupId).segments
        ).findIndex((s) => s.id === key)}`
      ] = value
    })

    return {
      ...phrase,
      id: `phr_tmp_${idx}`,
      segmentGroupId: `dim_tmp_${Array.from(segmentGroupIds).indexOf(
        phrase.segmentGroupId
      )}`,
      map
    }
  })

  offers = offers.map((offer) => {
    return {
      ...offer,
      parts: Object.fromEntries(
        Object.entries(offer.parts).map(([key, value]) => {
          return [
            key,
            value?.replace(
              /phr_[a-zA-Z0-9]+/g,
              (match) => `phr_tmp_${Array.from(phraseIds).indexOf(match)}`
            )
          ]
        })
      )
    }
  })

  questions = questions.map((question) => {
    return {
      ...question,
      text: question.text.replace(
        /phr_[a-zA-Z0-9]+/g,
        (match) => `phr_tmp_${Array.from(phraseIds).indexOf(match)}`
      )
    }
  })

  // Then reprocess to sub in duplicates

  funnelDefinitionEach(definition, (node) => {
    if (node.type === 'sg' && node.segmentGroupId) {
      if (node.split) {
        node.split = node.split.map((s) => {
          if (!s.segmentId) return s
          return {
            ...s,
            segmentId: `seg_tmp_${Array.from(segmentGroupIds).indexOf(
              node.segmentGroupId
            )}_${Array.from(
              segmentGroupStore.getById(node.segmentGroupId).segments
            ).findIndex((seg) => seg.id === s.segmentId)}`
          }
        })
      }

      node.segmentGroupId = `dim_tmp_${Array.from(segmentGroupIds).indexOf(
        node.segmentGroupId
      )}`
    }

    if (node.type === 'offer' && node.offerId) {
      node.offerId = `ofr_tmp_${Array.from(offerIds).indexOf(node.offerId)}`
    }

    if (node.type === 'yesno') {
      conditionEach(node.condition, (def) => {
        if (def.$source === 'segments') {
          const segmentGroupId = def.$type
          def.$type = `dim_tmp_${Array.from(segmentGroupIds).indexOf(
            segmentGroupId
          )}`

          def.segmentId = def.segmentId.map((id) => {
            return `seg_tmp_${Array.from(segmentGroupIds).indexOf(
              segmentGroupId
            )}_${Array.from(
              segmentGroupStore.getById(segmentGroupId).segments
            ).findIndex((s) => s.id === id)}`
          })
        }
      })
    }
  })

  // get the list of widgets
  const widgetStore = useWidgetStore()
  let widgets = widgetStore.getWidgetsForOfferFunnel(flow.id)

  widgets = widgets.map((widget, idx) => {
    return {
      id: `wgt_tmp_${idx}`,
      type: widget.type,
      name: widget.name,
      theme: widget.options.theme,
      contentId: `ofn_tmp_0`
    }
  })

  return {
    flow: {
      id: 'ofn_tmp_0',
      name: flow.name,
      funnelDefinition: definition
    },
    widgets,
    segmentGroups,
    questions,
    offers,
    phrases
  }
}

export const createTemplateFromQuestion = () => {}

export const createTemplateFromSegmentGroup = () => {}
